import React, { useState, useRef } from 'react';
import {
  Image as ImageIcon,
  UploadCloud,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  ArrowLeft,
  Trash2,
  Sparkles,
  RefreshCw,
} from 'lucide-react';
import { processCoverArt, SUPPORTED_IMAGE_EXTENSIONS } from '../../lib/coverArtProcessor';

export default function Step4CoverArt({
  coverArt,
  onUpdateCoverArt,
  onContinue,
  onBack,
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progressState, setProgressState] = useState({ progress: 0, stage: '', message: '' });
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setError('');
    setProcessing(true);
    setProgressState({ progress: 10, stage: 'reading', message: 'Loading image file...' });

    try {
      const result = await processCoverArt(file, (status) => {
        setProgressState({
          progress: status.progress,
          stage: status.stage,
          message: status.message,
        });
      });

      onUpdateCoverArt(result);
    } catch (err) {
      setError(err.message || 'Cover art processing failed.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files?.[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemove = () => {
    onUpdateCoverArt(null);
    setError('');
  };

  const canContinue = Boolean(coverArt && (coverArt.processedFile || coverArt.previewUrl || coverArt.url));

  return (
    <div className="dist-card">
      <div className="dist-card-header">
        <div>
          <h2 className="dist-card-title">
            <ImageIcon size={22} color="#d9fb06" /> Cover Artwork
          </h2>
          <p className="dist-card-desc">
            Upload exactly 1 high-resolution cover artwork. Artwork will automatically be verified for 1:1 square aspect ratio and exported to pristine 3000 × 3000 px JPG.
          </p>
        </div>
      </div>

      {error && (
        <div
          style={{
            background: 'rgba(255, 80, 80, 0.1)',
            border: '1px solid rgba(255, 80, 80, 0.3)',
            borderRadius: '10px',
            padding: '1rem',
            marginBottom: '1.5rem',
            color: '#ff8585',
            fontSize: '0.85rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
          }}
        >
          <AlertCircle size={18} /> {error}
        </div>
      )}

      {/* Processing Progress Bar */}
      {processing && (
        <div className="dist-audio-track-item" style={{ marginBottom: '1.5rem' }}>
          <div className="dist-track-top-row">
            <div className="dist-track-name-badge">
              <RefreshCw size={18} color="#d9fb06" className="animate-spin" />
              <span className="dist-track-filename">{progressState.message}</span>
            </div>
            <span style={{ fontSize: '0.8rem', color: '#d9fb06', fontWeight: 600 }}>
              {progressState.progress}%
            </span>
          </div>
          <div className="dist-progress-bar-container">
            <div className="dist-progress-bar-fill" style={{ width: `${progressState.progress}%` }} />
          </div>
        </div>
      )}

      {/* Existing Processed Cover Preview */}
      {coverArt && (coverArt.previewUrl || coverArt.url) ? (
        <div className="dist-cover-preview-wrapper" style={{ marginBottom: '2rem' }}>
          <div className="dist-cover-image-box">
            <img src={coverArt.previewUrl || coverArt.url} alt="Processed Cover Art" />
          </div>

          <div style={{ flex: 1, minWidth: '240px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
              <CheckCircle2 size={20} color="#d9fb06" />
              <h4 style={{ fontSize: '1.15rem', fontWeight: 700, margin: 0, color: '#ffffff' }}>
                Cover Art Verified &amp; Ready
              </h4>
            </div>

            <p style={{ fontSize: '0.85rem', color: 'rgba(255, 255, 255, 0.7)', margin: '0 0 1rem', lineHeight: 1.5 }}>
              Original File: <strong>{coverArt.originalFilename || 'Cover Artwork'}</strong>
              {coverArt.isCropped && (
                <span style={{ display: 'block', color: '#d9fb06', fontSize: '0.8rem', marginTop: '0.2rem' }}>
                  ✓ Non-distorting 1:1 center crop applied.
                </span>
              )}
            </p>

            <div className="dist-track-tech-chips" style={{ marginBottom: '1.2rem' }}>
              <span className="dist-tech-chip accent">3000 × 3000 px</span>
              <span className="dist-tech-chip">1:1 Square</span>
              <span className="dist-tech-chip">JPEG High-Res</span>
              <span className="dist-tech-chip">RGB Color Space</span>
            </div>

            <div style={{ display: 'flex', gap: '0.8rem' }}>
              <button
                type="button"
                className="dist-btn-secondary"
                style={{ padding: '0.45rem 1rem', fontSize: '0.85rem' }}
                onClick={() => fileInputRef.current?.click()}
              >
                Replace Image
              </button>

              <button
                type="button"
                className="dist-remove-btn"
                onClick={handleRemove}
                title="Remove Cover"
                style={{ width: 'auto', padding: '0 0.8rem', gap: '0.3rem' }}
              >
                <Trash2 size={15} /> Remove
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Upload Dropzone */
        <div
          className={`dist-upload-dropzone ${isDragging ? 'drag-active' : ''}`}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
        >
          <div className="dist-upload-icon-circle">
            <UploadCloud size={28} />
          </div>

          <h3 className="dist-upload-heading">Choose or Drag Cover Artwork</h3>
          <p className="dist-upload-hint">
            Supports JPG, PNG, WEBP • Auto-converts and scales to 3000 × 3000 px JPG
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={SUPPORTED_IMAGE_EXTENSIONS.join(',')}
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.[0]) handleFile(e.target.files[0]);
          e.target.value = '';
        }}
      />

      {/* DSP Cover Requirements Note */}
      <div style={{ background: 'rgba(0,0,0,0.2)', padding: '1rem 1.2rem', borderRadius: '10px', marginTop: '1.5rem', border: '1px solid rgba(255,255,255,0.06)' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 600, color: '#d9fb06', marginBottom: '0.3rem' }}>
          Official DSP Artwork Guidelines (Spotify / Apple Music / Amazon):
        </div>
        <ul style={{ margin: 0, paddingLeft: '1.2rem', fontSize: '0.8rem', color: 'rgba(255,255,255,0.7)', lineHeight: 1.6 }}>
          <li>Must be square (1:1 ratio) and at least 3000 × 3000 pixels.</li>
          <li>Do not include pricing, barcodes, website URLs, social media handles, or platform logos (e.g. Spotify logo).</li>
          <li>Text must strictly match the Release Title and Primary Artist name.</li>
        </ul>
      </div>

      {/* Footer Navigation */}
      <div className="dist-footer-actions">
        <button type="button" className="dist-btn-secondary" onClick={onBack}>
          <ArrowLeft size={18} /> Back to Track Details
        </button>

        <button
          type="button"
          className="dist-btn-primary"
          onClick={onContinue}
          disabled={!canContinue || processing}
        >
          Continue to Review <ArrowRight size={18} />
        </button>
      </div>
    </div>
  );
}
